import { formatCodeableConcept } from '@medplum/core'
import type { CodeableConcept, Task } from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import type { CodingValue } from '#src/codeableConcept.ts'
import { invariant } from '#src/invariant.ts'
import {
  type TaskConfig,
  TaskConfigType,
} from '#src/models/tasks/taskConfigType.ts'

/**
 * Required Codings
 */
const REQUIRED_CODING_SYSTEM = 'required'
const REQUIRED_CODING_DISPLAYS = {
  true: 'Required',
  false: 'Optional',
} as const
export function makeRequiredCoding(required: boolean): CodingValue {
  const code = required ? 'true' : 'false'
  const display = REQUIRED_CODING_DISPLAYS[code]
  return {
    system: REQUIRED_CODING_SYSTEM,
    code,
    display,
  }
}
export function getRequiredFromCode(
  code: CodeableConcept | undefined
): boolean | undefined {
  const requiredCoding = code?.coding?.find(
    (coding) => coding.system === REQUIRED_CODING_SYSTEM
  )
  if (requiredCoding) {
    return requiredCoding.code === 'true'
  }
  // If the required coding is not found, return undefined to allow callsites to handle the case
  // where the required coding is not present
  return undefined
}

/**
 * Type Codings, including type-specific codings
 */
export function makeTypeSpecificCodings(taskConfig: TaskConfig): CodingValue[] {
  const typeCoding = makeTypeCoding(taskConfig.type)
  let additionalCodings: CodingValue[]
  switch (taskConfig.type) {
    case TaskConfigType.Simple:
    case TaskConfigType.Text:
    case TaskConfigType.UploadDocument:
      additionalCodings = []
      break
    case TaskConfigType.QuestionnaireResponse:
      additionalCodings = [makeQuestionnaireCoding(taskConfig.questionnaireUrl)]
      break
    case TaskConfigType.Enum:
      additionalCodings = [makeEnumOptionsCoding(taskConfig.options)]
      break
  }
  return [typeCoding, ...additionalCodings]
}

const TYPE_CODING_SYSTEM = 'type'
export function makeTypeCoding(type: TaskConfig['type']): CodingValue {
  return {
    system: TYPE_CODING_SYSTEM,
    code: type,
    display: type,
  }
}

export function getTypeFromCode(
  code: CodeableConcept | undefined
): TaskConfig['type'] | undefined {
  return code?.coding?.find((coding) => coding.system === TYPE_CODING_SYSTEM)
    ?.code as TaskConfig['type'] | undefined
}

const QUESTIONNAIRE_CODING_SYSTEM = 'questionnaire'
export function makeQuestionnaireCoding(questionnaireUrl: string): CodingValue {
  return {
    system: QUESTIONNAIRE_CODING_SYSTEM,
    code: questionnaireUrl,
    display: questionnaireUrl,
  }
}

export function getQuestionnaireUrlFromCode(
  code: CodeableConcept | undefined
): string | undefined {
  return code?.coding?.find(
    (coding) => coding.system === QUESTIONNAIRE_CODING_SYSTEM
  )?.code
}

const ENUM_OPTIONS_CODING_SYSTEM = 'enumOptions'
export function makeEnumOptionsCoding(options: string[]): CodingValue {
  return {
    system: ENUM_OPTIONS_CODING_SYSTEM,
    code: options.join(','),
    display: options.join(','),
  }
}

export function getEnumOptionsFromCode(
  code: CodeableConcept | undefined
): string[] | undefined {
  return code?.coding
    ?.find((coding) => coding.system === ENUM_OPTIONS_CODING_SYSTEM)
    ?.code?.split(',')
    .map((x) => x.trim())
}

export function makeCodingsFromTaskConfig(
  taskConfig: TaskConfig
): CodingValue[] {
  return compact([
    makeRequiredCoding(taskConfig.required),
    ...makeTypeSpecificCodings(taskConfig),
  ])
}

export function getTaskConfigFromTask(task: Task): TaskConfig | undefined {
  return getTaskConfigFromCode(task.code)
}

export function getTaskConfigFromCode(
  code: CodeableConcept | undefined
): TaskConfig {
  const type = getTypeFromCode(code)
  invariant(type, `Type not found from code ${formatCodeableConcept(code)}`)

  const required = getRequiredFromCode(code) ?? false

  switch (type) {
    case TaskConfigType.Simple:
    case TaskConfigType.Text:
    case TaskConfigType.UploadDocument:
      return {
        type,
        required,
      }
    case TaskConfigType.QuestionnaireResponse: {
      const questionnaireUrl = getQuestionnaireUrlFromCode(code)
      invariant(
        questionnaireUrl,
        `Questionnaire URL not found from code ${formatCodeableConcept(code)}`
      )
      return {
        type,
        required,
        questionnaireUrl,
      }
    }
    case TaskConfigType.Enum: {
      const options = getEnumOptionsFromCode(code)
      invariant(
        options,
        `Enum options not found from code ${formatCodeableConcept(code)}`
      )
      return {
        type,
        required,
        options,
      }
    }
  }
}
export function getTaskConfigFromCodeSafe(code: CodeableConcept | undefined):
  | {
      config: TaskConfig
      error: null
    }
  | {
      config: undefined
      error: Error
    } {
  try {
    return {
      config: getTaskConfigFromCode(code),
      error: null,
    }
  } catch (e) {
    return {
      config: undefined,
      error: e instanceof Error ? e : new Error(String(e)),
    }
  }
}
